import {create, CancelToken, ApiResponse as ApiSauceResponse} from 'apisauce';
import store from './store';
import apiServices from './apiServices';
import translationService from './translationService';
import {startLoading, finishLoading} from './actions/shared';
import {setAuthorizationData} from './actions/authorization';
import {setServerErrorStatus} from './actions/general';
import {getAccessToken, getAuthorizationStatus, getRefreshToken} from './selectors/authorization';
import {
    API_DOMAIN,
    CANCEL_ERROR,
    SERVER_ERROR,
    HTTP_CODE_TOO_MANY_REQUESTS,
    HTTP_CODE_UNAUTHORIZED,
    OAUTH_CLIENT_ID,
    OAUTH_CLIENT_SECRET
} from './constants';
import {getDeviceId, equal} from './utils';

const OAUTH_CLIENT_TOKEN = btoa(`${OAUTH_CLIENT_ID}:${OAUTH_CLIENT_SECRET}`);

const api = create({
    baseURL: API_DOMAIN,
    headers: {
        'Content-Type': 'application/json',
        'X-Platform': 'web',
        'Device-ID': `${getDeviceId()}`,
        'app-version': '2.0'
    }
});

const normalizeResponse = response => {
    const {ok, data} = response;

    response.isSuccess = ok;
    response.data = data || {};
};

const getBearerAccessToken = () => {
    const accessToken = getAccessToken(store.getState());

    return accessToken ? `Bearer ${accessToken}` : null;
};

const addAuthorizationHeader = request => {
    const bearerAccessToken = getBearerAccessToken();

    if (!bearerAccessToken || request.noRefresh) {
        return;
    }

    request.headers = {...request.headers, Authorization: bearerAccessToken};
};

const addAcceptLanguageHeader = request => {
    request.headers = {...request.headers, 'Accept-Language': translationService.getLanguage()};
};

const sources = {};
const handleCancelableRequest = request => {
    const {url, isCancelable} = request;

    if (!isCancelable) {
        return false;
    }

    if (sources[url]) {
        sources[url].cancel();
    }

    sources[url] = CancelToken.source();
    request.cancelToken = sources[url].token;
};

const ensureTokenFreshness = async response => {
    const {config, status, problem} = response;
    const isCancelledRequest = equal(CANCEL_ERROR, problem);

    if (isCancelledRequest || config.noRefresh) {
        return;
    }

    const state = store.getState();
    const isAuthorized = getAuthorizationStatus(state);
    const isAuthorizationError = equal(status, HTTP_CODE_UNAUTHORIZED);

    if (!isAuthorized || !isAuthorizationError) {
        return;
    }

    const bearerAccessToken = getBearerAccessToken();

    if (equal(config.headers.Authorization, bearerAccessToken)) {
        const refreshToken = getRefreshToken(state);
        const refreshData = new FormData();
        refreshData.append('refresh_token', refreshToken);
        refreshData.append('grant_type', 'refresh_token');

        const refreshConfig = {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Basic ${OAUTH_CLIENT_TOKEN}`
            },
            noRefresh: true
        };

        const {ok: isRefreshSuccess, status: refreshStatusCode, data: refreshedData} = await apiServices.refreshToken({refreshData, refreshConfig});

        if (!isRefreshSuccess) {
            console.log(`Refresh token has failed with: ${refreshStatusCode}, body: ${JSON.stringify(refreshedData)}`);
            return;
        }

        store.dispatch(setAuthorizationData(refreshedData));
    }

    const {method, baseURL, url: fullUrl, ...restConfig} = config;
    const url = fullUrl.replace(new RegExp(`^${baseURL}`), '');

    const newResponse = await api[method](url, null, {...restConfig, fresh: true});

    Object.assign(response, newResponse);
};

const showLoader = ({isLoader = true}) => store.dispatch(startLoading(isLoader));
const hideLoader = () => store.dispatch(finishLoading());

const handleServerError = ({status, problem}) => {
    const isServerError = equal(SERVER_ERROR, problem);

    if (!isServerError) {
        return false;
    }

    store.dispatch(setServerErrorStatus(status));
};

const handleTooManyRequestsError = ({status}) => {
    const isTooManyRequestsError = equal(status, HTTP_CODE_TOO_MANY_REQUESTS);

    if (!isTooManyRequestsError) {
        return false;
    }

    store.dispatch(setServerErrorStatus(status));
};

// @ts-ignore
api.addRequestTransform(showLoader);
api.addRequestTransform(addAuthorizationHeader);
api.addRequestTransform(addAcceptLanguageHeader);
api.addRequestTransform(handleCancelableRequest);

api.addAsyncResponseTransform(ensureTokenFreshness);
api.addResponseTransform(normalizeResponse);
api.addResponseTransform(hideLoader);

// @ts-ignore
api.addMonitor(handleServerError);
// @ts-ignore
api.addMonitor(handleTooManyRequestsError);

type ApiResponse<T, U = T> = ApiSauceResponse<T, U> & {
    isSuccess?: boolean
};

export type {ApiResponse};
export default api;
